import { useEffect, useState, lazy, Suspense } from 'react';
import { BellRing, BellOff, UserPlus, MessageSquare, FileCheck2, FileText } from 'lucide-react';
import { Badge } from '@/components/ui/badge.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog.jsx';
import { api, ApiError } from '@/lib/api.js';
import { toastSmsWarnings } from '@/lib/smsWarnings.js';
import { usePeekStore } from '@/stores/peek-store.js';
import { openContactPeek } from '@/lib/links.js';
import { labelsForRatings } from '@/lib/ratings.js';
import useDeal from '@/hooks/useDeal.js';
import ComposeSheet from '@/components/comms/ComposeSheet.jsx';
import { sendPilotNudge } from '@/services/comms.service.js';
import { listPaperPHFs, confirmPaperPHF, dismissPaperPHF } from '@/services/account.service.js';
import ConflictPanel from '@/components/roster/ConflictPanel.jsx';
import PhfSubmissionSheet from '@/components/sheets/PhfSubmissionSheet.jsx';
import MakePersonSheet from '@/components/people/MakePersonSheet.jsx';
import { PILOT_INTERESTS } from '@/lib/pilot-interests.js';
import Panel from './Panel.jsx';
import AddPilotLinkButton from './AddPilotLinkButton.jsx';
import { formatDateOrEmpty } from '@/lib/date-display.js';

// PILOT-DOORS Ship B (one door): every add-pilot affordance opens the
// RosterSheet — its add card is the ONE broker surface for pilot adds.
// Lazy like the DealActionsSheets mount (heavy sheet, bundle size).
const RosterSheet = lazy(() => import('@/components/sheets/RosterSheet.jsx'));
// Co-owner chain: an Owner added through this door prompts the named-insured
// / entity update (default Partnership) after the roster sheet closes — the
// same behavior as the deal action bar door, never a stacked overlay.
const EditNamedInsuredSheet = lazy(() => import('@/components/sheets/EditNamedInsuredSheet.jsx'));
// BNO Quote Desk (TRACON-BNO-QUOTE-DESK-SPEC.md): quote a non-owned/CFI
// policy from this pilot's Living PHF. Heavy sheet, lazy like the others.
const BnoQuoteDeskSheet = lazy(() => import('@/components/sheets/BnoQuoteDeskSheet.jsx'));

// Split a roster row's display name into first/last for the add-card
// prefill. Best-effort: first token is the first name, the rest is the last.
function splitName(name) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first_name: '', last_name: '' };
  if (parts.length === 1) return { first_name: parts[0], last_name: '' };
  return { first_name: parts[0], last_name: parts.slice(1).join(' ') };
}

// Shape: roster entries are
//   { crm_contact_id, name, email, certificate_type, ratings,
//     total_flight_hours, medical_class, medical_expires,
//     phf_status, status }
//
// 2026-05-20 (Vision §V2 fix #2 — per-pilot Remind launcher): each
// pilot row gets a BellRing icon button. Click opens ComposeSheet
// pre-selected to the pilot-reminder template with pilot_crm_id set
// to that specific pilot's CRM Contact id, so the preview body, the
// recipient, and the addressed first name all line up to the broker-
// picked pilot (not the auto-pick of first-Pending PHF).

const PHF_STATUS_VARIANT = {
  verified: 'success',
  submitted: 'info',
  in_progress: 'warning',
  invited: 'outline',
  removed: 'muted',
};

export default function RosterPanel({ roster, dealId, onChange }) {
  // Former pilots are QUARANTINED, never rendered as peers of the live
  // roster: the main list is current-only; historical rows live in a
  // collapsed "Former: not on current policy" section below.
  const rows = (roster || []).filter((p) => p.status !== 'historical');
  const formerRows = (roster || []).filter((p) => p.status === 'historical');
  // useDeal is inFlight-deduped at the service layer, so DealActionBar
  // + this RosterPanel sharing a dealId only fires one network request.
  const { data: dealRes } = useDeal(dealId || null);
  const sessionToken = dealRes?.deal?.session_token || null;
  const accountName = dealRes?.deal?.named_insured || dealRes?.deal?.account_name || '';
  // `remindPilot` opens ComposeSheet against a chosen pilot. Holds
  // the full pilot row so we can show the right name/email in the toast
  // even after the sheet closes.
  const openPeek = usePeekStore((s) => s.openPeek);
  const [remindPilot, setRemindPilot] = useState(null);
  // Roster invite entry point (Gap 1a → PILOT-DOORS Ship B): opens the
  // RosterSheet (the one door). `addPilotPrefill` carries the picked row's
  // identity so the broker confirms instead of retyping; null prefill = the
  // blank "Add / invite pilot" header action.
  const [rosterOpen, setRosterOpen] = useState(false);
  const [addPilotPrefill, setAddPilotPrefill] = useState(null);
  // Co-owner chain state: set when an Owner is added via the roster door,
  // opens EditNamedInsuredSheet (pre-set Partnership) once the roster closes.
  const [coOwnerPending, setCoOwnerPending] = useState(false);
  const [editInsuredOpen, setEditInsuredOpen] = useState(false);
  // Per-client pause for the scheduled primary auto-nudge (Gap 1b). Seeded
  // from the deal (default on); optimistic flip on toggle.
  const [nudgesEnabled, setNudgesEnabled] = useState(true);
  const [savingNudgePref, setSavingNudgePref] = useState(false);
  // Per-pilot SMS reminder (Gap 2). Routes through the canonical /nudge endpoint
  // (server owns the body + logging). Confirm first since it fires a real text.
  // The pilot whose submitted PHF is open (prompt 69 item 4). Null = closed.
  const [viewPhf, setViewPhf] = useState(null);
  // Request-approval door (158 §8 item 1): the pilot awaiting the broker's
  // confirm, and the in-flight guard.
  const [approvalPilot, setApprovalPilot] = useState(null);
  const [requestingApproval, setRequestingApproval] = useState(false);
  // The pilot whose BNO quote desk is open (spec entry point: the pilot record).
  const [bnoPilot, setBnoPilot] = useState(null);
  // The contact-less pilot whose "Make a person" sheet is open (C8). Null = closed.
  const [makePilot, setMakePilot] = useState(null);
  const [smsPilot, setSmsPilot] = useState(null);
  const [sendingSms, setSendingSms] = useState(false);
  useEffect(() => {
    if (dealRes?.deal && typeof dealRes.deal.primary_nudges_enabled === 'boolean') {
      setNudgesEnabled(dealRes.deal.primary_nudges_enabled);
    }
  }, [dealRes?.deal?.primary_nudges_enabled]);

  // PAPER-PHF-HOLDING: paper submissions wait here (no CRM Contact, not on
  // the roster) until the broker confirms or dismisses. Confirm links/creates
  // the Contact and lands the pilot through applyPilot; a mid-term add is an
  // endorsement, so the broker clicks only after the UW/premium legwork.
  const [paperPhfs, setPaperPhfs] = useState([]);
  const [paperAction, setPaperAction] = useState(null); // { row, kind: 'confirm'|'dismiss' }
  const [paperInterest, setPaperInterest] = useState('Permissive User');
  const [paperBusy, setPaperBusy] = useState(false);
  const [paperConflict, setPaperConflict] = useState(null); // 409 body from confirm

  async function refreshPaperPhfs() {
    if (!dealId) return;
    try {
      const data = await listPaperPHFs(dealId);
      setPaperPhfs(data.paper_phfs || []);
    } catch {
      // Non-blocking: the strip just stays empty; the roster itself is fine.
    }
  }
  useEffect(() => { refreshPaperPhfs(); }, [dealId]);

  async function doPaperConfirm(resolution) {
    if (!paperAction?.row) return;
    setPaperBusy(true);
    try {
      const body = { interest: paperInterest || undefined, ...(resolution ? { resolution } : {}) };
      const res = await confirmPaperPHF(dealId, paperAction.row.id, body);
      toast.success(`${paperAction.row.first_name} ${paperAction.row.last_name} added to the roster${
        res?.approved_pilots_added ? ` (${res.approved_pilots_added} aircraft)` : ''}`);
      (res?.warnings || []).forEach((w) => toast.info(w)); // R20: a reuse said out loud, never console
      setPaperAction(null);
      setPaperConflict(null);
      refreshPaperPhfs();
      if (typeof onChange === 'function') onChange();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.body?.conflict && err.body.conflict !== 'duplicate_phf') {
        setPaperConflict(err.body);
      } else {
        toast.error(err instanceof ApiError ? (err.body?.error || err.message) : 'Failed to confirm pilot');
      }
    } finally {
      setPaperBusy(false);
    }
  }

  async function doPaperDismiss() {
    if (!paperAction?.row) return;
    setPaperBusy(true);
    try {
      await dismissPaperPHF(dealId, paperAction.row.id);
      toast.success('Paper form dismissed');
      setPaperAction(null);
      refreshPaperPhfs();
    } catch (err) {
      toast.error(err instanceof ApiError ? (err.body?.error || err.message) : 'Failed to dismiss');
    } finally {
      setPaperBusy(false);
    }
  }

  function openInvite(prefill) {
    setAddPilotPrefill(prefill || null);
    setRosterOpen(true);
  }

  async function toggleNudges() {
    if (!sessionToken) {
      toast.error('This deal has no TRACON session yet — initiate one first.');
      return;
    }
    const next = !nudgesEnabled;
    setNudgesEnabled(next); // optimistic
    setSavingNudgePref(true);
    try {
      await api.patch(`/api/tracon/session/${sessionToken}/nudge-pref`, { enabled: next });
      toast.success(next ? 'Primary reminders resumed for this client' : 'Primary reminders paused for this client');
    } catch (err) {
      setNudgesEnabled(!next); // revert
      toast.error(err instanceof ApiError ? (err.body?.error || err.message) : 'Failed to update reminder setting');
    } finally {
      setSavingNudgePref(false);
    }
  }

  async function doSendText() {
    if (!smsPilot || !sessionToken) return;
    setSendingSms(true);
    try {
      const res = await sendPilotNudge(sessionToken, smsPilot.crm_contact_id, { channel: 'sms' });
      toast.success(`Texted ${smsPilot.name || 'pilot'}`);
      toastSmsWarnings(res?.warnings);
      setSmsPilot(null);
      if (typeof onChange === 'function') onChange();
    } catch (err) {
      toast.error(err instanceof ApiError ? (err.body?.error || err.message) : 'Failed to send text');
    } finally {
      setSendingSms(false);
    }
  }

  function handleRemind(pilot) {
    // Invited pilot with a live Tower session → keep the ComposeSheet preview
    // (lets the broker edit before sending). Otherwise (no CRM contact id yet,
    // or no session) route to the RosterSheet add card (the one door), seeded
    // with the row's identity — it create-or-matches the Contact and issues
    // the PHF invite on the explicit invite choice.
    if (pilot?.crm_contact_id && sessionToken) {
      setRemindPilot(pilot);
      return;
    }
    openInvite({ ...splitName(pilot?.name), email: pilot?.email || '', phone: pilot?.phone || '' });
  }

  // Writes the pending endorsement that Yellow reads: type endorsement,
  // status pending_carrier_statement, premium OMITTED (the carrier has not
  // answered), changes.pilots_added naming the pilot. Effective date is the
  // REQUEST date, which the server's endorsement window decides (today
  // Eastern, or the term's start when it has not begun; this page derives
  // no day); the real effective lands through the approve door
  // (recordEndorsementApproved) when the carrier answers. Broker-only:
  // nothing here emails a client or a carrier.
  async function handleRequestApproval() {
    const p = approvalPilot;
    if (!p || requestingApproval || !dealId) return;
    setRequestingApproval(true);
    try {
      const { createTransaction } = await import('@/services/deals.service.js');
      const res = await createTransaction(dealId, {
        type: 'endorsement',
        status: 'pending_carrier_statement',
        description: `Pilot approval requested: ${p.name || p.crm_contact_id}`,
        changes: { pilots_added: [{ contact_id: String(p.crm_contact_id), name: p.name || null }] },
        policy_snapshot: { pending: 'pilot_approval', pilot: p.name || String(p.crm_contact_id) },
      });
      if (res?.success === false) throw new Error(res?.error || 'Request failed');
      toast.success(`Approval request recorded for ${p.name || 'pilot'}. The roster shows Seeking approval until the carrier answers.`);
      for (const w of res?.warnings || []) toast.info(w);
      setApprovalPilot(null);
      onChange?.();
    } catch (err) {
      toast.error(`Approval request failed: ${err?.body?.error || err.message || 'unknown error'}`);
    } finally {
      setRequestingApproval(false);
    }
  }

  return (
    <>
      <Panel
        title="Pilots"
        count={rows.length}
        empty={rows.length === 0 && paperPhfs.length === 0 ? 'No pilots on file.' : null}
        footer={
          dealId ? (
            <div className="flex items-center justify-between gap-2">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => openInvite(null)}
                className="h-7 gap-1.5 px-2 text-text-muted hover:text-accent"
              >
                <UserPlus size={13} strokeWidth={1.5} />
                Add / invite pilot
              </Button>
              <AddPilotLinkButton dealId={dealId} />
              {sessionToken ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={savingNudgePref}
                  onClick={toggleNudges}
                  title={nudgesEnabled
                    ? 'Pause the scheduled email that asks this client to chase their pilots'
                    : 'Resume the scheduled email that asks this client to chase their pilots'}
                  className="h-7 gap-1.5 px-2 text-text-dim hover:text-accent"
                >
                  {nudgesEnabled ? <BellRing size={13} strokeWidth={1.5} /> : <BellOff size={13} strokeWidth={1.5} />}
                  {nudgesEnabled ? 'Reminders on' : 'Reminders paused'}
                </Button>
              ) : null}
            </div>
          ) : null
        }
      >
        {paperPhfs.length > 0 && (
          <div className="mb-3 space-y-2">
            {paperPhfs.map((r) => (
              <div key={r.id} className="rounded-card border border-warning/40 bg-warning/5 px-3 py-2.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <FileCheck2 size={13} strokeWidth={1.5} className="self-center text-warning" />
                      <span className="text-sm font-semibold text-text">
                        {[r.first_name, r.last_name].filter(Boolean).join(' ') || 'Unnamed pilot'}
                      </span>
                      {/* Say which door he came through. A pilot who filled his
                          own form link is not a paper form, and calling him one
                          hid the fact that intake pilots land here at all. */}
                      {r.submission_source === 'paper_phf' ? (
                        <Badge variant="warning" className="text-[9px]" title="Paper history form submitted, not on the roster or in CRM until you confirm">
                          PAPER PHF HOLDING
                        </Badge>
                      ) : (
                        <Badge variant="warning" className="text-[9px]" title="Submitted their own pilot history form but has no CRM contact yet. Confirm to create or link the contact so they can be offered and named on the policy.">
                          SUBMITTED, NOT IN CRM
                        </Badge>
                      )}
                    </div>
                    <div className="mt-0.5 flex flex-wrap gap-x-3 text-[11px] text-text-dim">
                      {r.email ? <span className="truncate">{r.email}</span> : null}
                      {r.tails?.length ? <span>{r.tails.join(', ')}</span> : null}
                      {r.submitted_at ? <span>submitted {formatDateOrEmpty(r.submitted_at, { month: 'short', day: 'numeric', year: 'numeric' })}</span> : null}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <Button type="button" size="sm"
                      onClick={() => { setPaperAction({ row: r, kind: 'confirm' }); setPaperInterest('Permissive User'); setPaperConflict(null); }}
                      className="h-7 px-2.5 text-xs">
                      Confirm
                    </Button>
                    <Button type="button" size="sm" variant="ghost"
                      onClick={() => setPaperAction({ row: r, kind: 'dismiss' })}
                      className="h-7 px-2 text-xs text-text-dim hover:text-danger">
                      Dismiss
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <ul className="divide-y divide-border">
          {rows.map((p) => (
            <li key={p.crm_contact_id || p.roster_key || p.name} className="py-2.5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-baseline gap-2">
                    {/* Policy state, David's synthesis (2026-08-13): the NAME
                        wears the colour; only the EXCEPTIONS wear a word. On
                        policy is the normal case — green name, no badge, same
                        principle as the collapsed all-clear disclosures.
                        Seeking approval / Excluded / Undetermined colour the
                        name AND carry the word, because those rows need David
                        to do something. The word (with a tooltip) keeps
                        meaning off hue alone for the states that matter. A
                        null state (the server could not read the deal)
                        renders the default text colour and nothing else,
                        because a failed read is not an absence. */}
                    {(() => {
                      const nameTone = p.policy_state === 'on_policy' ? 'text-success'
                        : p.policy_state === 'seeking_approval' ? 'text-warning'
                        : p.policy_state === 'excluded' ? 'text-danger'
                        : 'text-text';
                      const title = p.policy_state === 'on_policy' ? 'On the policy (Approved Pilots)' : undefined;
                      return p.crm_contact_id ? (
                        <button
                          type="button"
                          onClick={() => openPeek(openContactPeek(p.crm_contact_id))}
                          title={title}
                          className={`text-sm font-semibold ${nameTone} hover:underline text-left`}
                        >
                          {p.name || '—'}
                        </button>
                      ) : (
                        <>
                          <span title={title} className={`text-sm font-semibold ${nameTone}`}>{p.name || '—'}</span>
                          {/* C8 (AR12): not a person yet, so nothing to edit;
                              this makes one (fix-pilot), and the name then
                              opens the person sheet like every other pilot. */}
                          {p.session_token && p.phf_token ? (
                            <button type="button" data-make-person onClick={() => setMakePilot(p)} className="text-[11px] font-medium text-accent hover:underline">
                              Make a person
                            </button>
                          ) : null}
                        </>
                      );
                    })()}
                    {p.policy_state === 'seeking_approval' ? (
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-warning">Seeking approval</span>
                    ) : p.policy_state === 'excluded' ? (
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-danger">Excluded</span>
                    ) : p.policy_state === 'undetermined' ? (
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-text-dim">Undetermined</span>
                    ) : null}
                    {/* A name that is not a link must say why, in words —
                        absence rendered as a missing affordance is still
                        absence spelled like an answer. */}
                    {!p.crm_contact_id ? (
                      <span className="text-[10px] text-text-dim">not in CRM yet</span>
                    ) : null}
                    {/* Historical rows never render here — they live in the
                        collapsed "Former" section below. */}
                  </div>
                  {p.email ? (
                    <a
                      href={`mailto:${p.email}`}
                      className="block truncate text-xs text-text-muted hover:text-accent"
                    >
                      {p.email}
                    </a>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {p.broker_entered ? (
                    <Badge variant="warning" className="text-[9px]" title="Added by broker: no client invite. What you entered counts as your attestation.">
                      BROKER-ENTERED
                    </Badge>
                  ) : null}
                  {/* THE PILOT SAID THIS, versus THIS IS WHAT WE HOLD.
                      PhfSubmissionSheet already draws that line one click deep,
                      and its own comment says the two must not look alike — but
                      the card carried only BROKER-ENTERED, so a `pilot_form`
                      row with no attestation rendered identically to a fully
                      attested one. Varnholt (an invented pilot) is the case.

                      Shown only on rows that CLAIM to be pilot-submitted:
                      broker-entered rows are already labelled above and two
                      badges saying overlapping things is how a surface stops
                      being read.

                      NEUTRAL, NOT A DEFECT. 71 April/May records predate TRACON
                      storing the answers; every submitted PHF since 1 June
                      carries one. That is a history, so this states what is
                      true and does not imply a failure. `outline`, not
                      `warning` — the warning variant is for things that need
                      doing. */}
                  {!p.broker_entered && p.phf_status && p.has_attestation === false ? (
                    <Badge
                      variant="outline"
                      className="text-[9px]"
                      title="TRACON holds this pilot's data but not their own submitted answers. Forms completed before June 2026 predate the stored attestation — a fresh PHF captures it."
                    >
                      NO ATTESTATION ON FILE
                    </Badge>
                  ) : null}
                  {p.phf_status ? (
                    <Badge
                      variant={PHF_STATUS_VARIANT[p.phf_status] || 'outline'}
                      className="text-[9px]"
                    >
                      {p.phf_status.replace(/_/g, ' ').toUpperCase()}
                    </Badge>
                  ) : null}
                  {/* READ WHAT THEY SUBMITTED. Shown whenever a PHF exists,
                      including historical rows: last term's answers are exactly
                      what a broker wants at renewal. Leads the icon group
                      because reading the form comes before chasing it. */}
                  {p.phf_id ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      aria-label={`Read what ${p.name || 'this pilot'} submitted`}
                      title={`Read ${p.name || 'this pilot'}'s submitted history form`}
                      onClick={() => setViewPhf({ id: p.phf_id, name: p.name })}
                      className="h-7 shrink-0 gap-1 px-1.5 text-[11px] font-medium text-text-dim hover:text-accent"
                    >
                      <FileText size={13} strokeWidth={1.5} />
                      Pilot history form
                    </Button>
                  ) : null}
                  {/* ONE button (Prompt 168): Read form and Correct opened the
                      identical screen, which is now an editable form — the
                      distinction the two buttons drew no longer exists. */}
                  {/* QUOTE NON-OWNED (TRACON-BNO-QUOTE-DESK-SPEC.md): open the
                      BNO quote desk prefilled from this pilot's Living PHF.
                      Needs a CRM Contact (the desk records against it and the
                      portal-link email sends through it). */}
                  {p.crm_contact_id ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      aria-label={`Quote non-owned insurance for ${p.name || 'this pilot'}`}
                      title={`Quote a non-owned (renter/CFI) policy for ${p.name || 'this pilot'} from their record`}
                      onClick={() => setBnoPilot(p)}
                      className="h-7 shrink-0 gap-1 px-1.5 text-[11px] font-medium text-text-dim hover:text-accent"
                    >
                      <FileCheck2 size={13} strokeWidth={1.5} />
                      Quote non-owned
                    </Button>
                  ) : null}
                  {/* REQUEST APPROVAL (158 §8 item 1, David's Yellow design):
                      writes the pending endorsement the Yellow state reads —
                      type endorsement, pending_carrier_statement, NULL premium
                      (the carrier has not answered; a placeholder must not be
                      spelled like an answer), changes.pilots_added naming the
                      pilot. Offered only when the pilot is not on the policy
                      and no request is already in flight. */}
                  {dealId && p.status === 'current' && p.crm_contact_id
                    && (p.policy_state === 'undetermined' || p.policy_state === 'excluded') ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      aria-label={`Request carrier approval for ${p.name || 'this pilot'}`}
                      title={`Ask the carrier to approve ${p.name || 'this pilot'} (records a pending endorsement; no client email)`}
                      onClick={() => setApprovalPilot(p)}
                      className="h-7 shrink-0 gap-1 px-1.5 text-[11px] font-medium text-text-dim hover:text-warning"
                    >
                      <UserPlus size={13} strokeWidth={1.5} />
                      Request approval
                    </Button>
                  ) : null}
                  {dealId && p.status !== 'historical' ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      aria-label={`Remind ${p.name || 'pilot'} by email`}
                      title={`Send pilot reminder email to ${p.name || 'this pilot'}`}
                      onClick={() => handleRemind(p)}
                      className="h-7 shrink-0 gap-1 px-1.5 text-[11px] font-medium text-text-dim hover:text-accent"
                    >
                      <BellRing size={13} strokeWidth={1.5} />
                      Remind
                    </Button>
                  ) : null}
                  {dealId && sessionToken && p.status !== 'historical' && p.crm_contact_id && p.phone ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      aria-label={`Text ${p.name || 'pilot'}`}
                      title={`Text the pilot reminder to ${p.name || 'this pilot'}`}
                      onClick={() => setSmsPilot(p)}
                      className="h-7 shrink-0 gap-1 px-1.5 text-[11px] font-medium text-text-dim hover:text-accent"
                    >
                      <MessageSquare size={13} strokeWidth={1.5} />
                      Text
                    </Button>
                  ) : null}
                </div>
              </div>
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-text-dim">
                {p.certificate_type ? <span>{p.certificate_type}</span> : null}
                {p.ratings ? <span>{labelsForRatings(p.ratings).join(', ')}</span> : null}
                {p.total_flight_hours ? (
                  <span title={p.hours_as_of ? `As the pilot reported it on ${String(p.hours_as_of).slice(0, 10)}. Not a live figure.` : 'No submission date recorded for this figure.'}>
                    {p.total_flight_hours} hrs
                    {/* THE DATE, NOT A WARNING. A stale figure and a wrong one
                        need different fixes: old wants the pilot asked again,
                        wrong wants a correction. Saying when he said it lets
                        David tell them apart without the screen guessing. */}
                    {p.hours_as_of ? <span className="text-text-dim"> as of {String(p.hours_as_of).slice(0, 10)}</span> : null}
                  </span>
                ) : null}
                {p.medical_class ? <span>{p.medical_class} medical</span> : null}
              </div>
              {/* Which signed form a carrier's answers come from, and its
                  signature date (David 2026-11-01). The server's words, full
                  width under the row. */}
              {p.answers_form?.words ? (
                <p className="mt-0.5 text-[11px] text-text-dim" data-answers-form={p.answers_form.reason}>
                  {p.answers_form.words}
                </p>
              ) : null}
            </li>
          ))}
          {rows.length === 0 ? (
            <li className="py-2.5 text-sm text-text-dim">No pilots on the current policy.</li>
          ) : null}
        </ul>

        {/* Former pilots: visually quarantined — collapsed by default, never
            peers of the live roster. Kept for history (old PHFs, prior terms,
            removed-at-bind pilots like the Stan Kasprzyk class). */}
        {formerRows.length > 0 ? (
          <details className="mt-2 rounded-card border border-border/60">
            <summary className="cursor-pointer list-none px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-text-dim hover:text-text-muted">
              Former: not on current policy ({formerRows.length})
            </summary>
            <ul className="divide-y divide-border/60 px-3 pb-2">
              {formerRows.map((p) => (
                <li key={p.crm_contact_id || p.roster_key || p.name} className="flex flex-wrap items-baseline gap-x-3 py-2 text-[12.5px] text-text-dim">
                  {p.crm_contact_id ? (
                    <button
                      type="button"
                      onClick={() => openPeek(openContactPeek(p.crm_contact_id))}
                      className="font-medium text-text-muted hover:underline"
                    >
                      {p.name || '—'}
                    </button>
                  ) : (
                    <>
                      <span className="font-medium text-text-muted">{p.name || '—'}</span>
                      {p.session_token && p.phf_token ? (
                        <button type="button" data-make-person onClick={() => setMakePilot(p)} className="text-[11px] text-accent hover:underline">
                          Make a person
                        </button>
                      ) : null}
                    </>
                  )}
                  {p.certificate_type ? <span className="text-[11px]">{p.certificate_type}</span> : null}
                  {p.total_flight_hours ? <span className="text-[11px]">{p.total_flight_hours} hrs</span> : null}
                  <span className="ml-auto text-[10px] uppercase tracking-wide">not on current policy</span>
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </Panel>

      <PhfSubmissionSheet
        phfId={viewPhf?.id || null}
        pilotName={viewPhf?.name || ''}
        open={!!viewPhf}
        onOpenChange={(o) => !o && setViewPhf(null)}
      />

      <MakePersonSheet
        target={makePilot ? { session_token: makePilot.session_token, phf_token: makePilot.phf_token } : null}
        prefill={makePilot ? { first_name: makePilot.first_name, last_name: makePilot.last_name, email: makePilot.email, phone: makePilot.phone } : null}
        open={!!makePilot} onOpenChange={(o) => { if (!o) setMakePilot(null); }} pilotName={makePilot?.name || ''}
        onMade={() => { if (typeof onChange === 'function') onChange(); }}
      />

      {bnoPilot ? (
        <Suspense fallback={null}>
          <BnoQuoteDeskSheet
            contactId={bnoPilot.crm_contact_id}
            pilotName={bnoPilot.name || ''}
            onClose={() => setBnoPilot(null)}
          />
        </Suspense>
      ) : null}

      <AlertDialog open={!!approvalPilot} onOpenChange={(o) => { if (!o) setApprovalPilot(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Request carrier approval for {approvalPilot?.name || 'this pilot'}?</AlertDialogTitle>
            <AlertDialogDescription>
              This records a pending endorsement on the ledger (no premium yet, the carrier has not
              answered) and the roster shows &quot;Seeking approval&quot; until you record the carrier&apos;s
              answer on the transaction. Nothing is emailed to the client or the carrier; sending the
              request itself stays your act.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={requestingApproval}>Not now</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleRequestApproval(); }}
              disabled={requestingApproval}
            >
              {requestingApproval ? 'Recording…' : 'Record the request'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ComposeSheet anchored to the per-pilot Remind action. Pre-
          selected to pilot-reminder and parameterized with the broker-
          picked pilot's CRM Contact id, which the backend template
          builder uses to render against the right pilot. */}
      <ComposeSheet
        sessionToken={sessionToken}
        dealId={dealId}
        open={!!remindPilot}
        onOpenChange={(o) => !o && setRemindPilot(null)}
        initialSlug="pilot-reminder"
        extra={
          remindPilot
            ? {
                pilot_id: remindPilot.crm_contact_id,
                pilot_crm_id: remindPilot.crm_contact_id,
              }
            : null
        }
        onSent={() => {
          setRemindPilot(null);
          if (typeof onChange === 'function') onChange();
        }}
      />

      {/* Roster invite entry point (Gap 1a → PILOT-DOORS Ship B): the one
          door. The RosterSheet's add card create-or-matches the Contact,
          handles the 409 conflict UI, and only emails on an explicit invite
          choice. On add/change we refresh the roster so the row appears. */}
      {rosterOpen && (
        <Suspense fallback={null}>
          <RosterSheet
            dealId={dealId}
            open={rosterOpen}
            onOpenChange={(o) => {
              setRosterOpen(o);
              if (!o && coOwnerPending) setEditInsuredOpen(true);
            }}
            accountName={accountName}
            stage={dealRes?.deal?.stage}
            onChange={() => { if (typeof onChange === 'function') onChange(); }}
            addPilotPrefill={addPilotPrefill}
            onPilotAdded={({ interest }) => {
              if (interest === 'Owner') setCoOwnerPending(true);
            }}
          />
        </Suspense>
      )}
      {editInsuredOpen && (
        <Suspense fallback={null}>
          <EditNamedInsuredSheet
            accountId={dealRes?.deal?.account_id || dealRes?.deal?.crm_account_id}
            cascadeDealId={dealId}
            initialName={accountName}
            initialPolicyholderType="Partnership"
            open={editInsuredOpen}
            onOpenChange={(o) => {
              setEditInsuredOpen(o);
              if (!o) setCoOwnerPending(false);
            }}
            onSaved={() => { if (typeof onChange === 'function') onChange(); }}
          />
        </Suspense>
      )}

      {/* Paper-PHF confirm / dismiss. Confirm creates or links the CRM
          Contact and lands the pilot on the roster (aircraft resolved
          server-side from the paper form's tail); the 409 handshake swaps in
          the ConflictPanel. Dismiss flips the form to removed. */}
      <AlertDialog open={!!paperAction} onOpenChange={(o) => { if (!o && !paperBusy) { setPaperAction(null); setPaperConflict(null); } }}>
        <AlertDialogContent>
          {paperConflict ? (
            <ConflictPanel
              conflict={paperConflict}
              busy={paperBusy}
              onResolve={(resolution) => doPaperConfirm(resolution)}
              onCancel={() => setPaperConflict(null)}
            />
          ) : paperAction?.kind === 'confirm' ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Add {[paperAction.row.first_name, paperAction.row.last_name].filter(Boolean).join(' ') || 'this pilot'} to the roster?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Creates or links their CRM contact, puts them on
                  {paperAction.row.tails?.length ? ` ${paperAction.row.tails.join(', ')}` : ' the policy aircraft'},
                  and syncs their submitted history. No email goes out. If this is a
                  mid-term add, make sure the underwriter has signed off first.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <label className="block">
                <span className="text-xs font-medium uppercase tracking-wide text-text-muted">Role</span>
                <select
                  value={paperInterest}
                  onChange={(e) => setPaperInterest(e.target.value)}
                  className="mt-1.5 flex w-full rounded-card border border-border bg-bg px-3 py-2 text-sm text-text focus:border-accent focus:outline-none"
                >
                  {PILOT_INTERESTS.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
                </select>
              </label>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={paperBusy}>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={(e) => { e.preventDefault(); doPaperConfirm(); }} disabled={paperBusy}>
                  {paperBusy ? 'Adding…' : 'Confirm and add to roster'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : paperAction ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {paperAction.row.submission_source === 'paper_phf' ? 'Dismiss this paper form?' : 'Dismiss this pilot history form?'}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Removes {[paperAction.row.first_name, paperAction.row.last_name].filter(Boolean).join(' ') || 'this pilot'}&apos;s
                  {paperAction.row.submission_source === 'paper_phf' ? ' paper submission' : ' submitted form'} from the holding area without adding them to the roster.
                  The form data stays on file (status: removed).
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={paperBusy}>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={(e) => { e.preventDefault(); doPaperDismiss(); }} disabled={paperBusy}>
                  {paperBusy ? 'Dismissing…' : 'Dismiss'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : null}
        </AlertDialogContent>
      </AlertDialog>

      {/* Per-pilot SMS reminder confirm (Gap 2). Fires a real text via /nudge,
          so confirm first. The body is the canonical server-side reminder. */}
      <AlertDialog open={!!smsPilot} onOpenChange={(o) => { if (!o && !sendingSms) setSmsPilot(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Text {smsPilot?.name || 'this pilot'}?</AlertDialogTitle>
            <AlertDialogDescription>
              Sends the standard pilot-history reminder by text to {smsPilot?.phone || 'their number on file'}.
              It logs to the timeline. Opted-out numbers send anyway with a warning.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={sendingSms}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); doSendText(); }}
              disabled={sendingSms}
            >
              {sendingSms ? 'Sending…' : 'Send text'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
